// SeasonVoucherPickerModal. Opens from a season-voucher mail in the
// mailbox. Lets the top-10 winner pick any portrait pack from the
// cosmetics store, then redeems the voucher for that pack with no
// coin cost. Mirrors the paid-purchase flow's "client rolls a random
// card from the pack's pool" pattern so the granted cosmetic stays
// random within the chosen pool, and visually mirrors the store's
// portrait pack tiles (shared fan preview + identical sizing) so the
// two surfaces feel like the same product.

import { Component } from 'preact';
import { toast } from 'sonner';
import RuneSpinner from './RuneSpinner';
import { fetchCosmetics, redeemSeasonVoucher } from '../utils/arena/cosmeticsApi';
import { noteCosmeticUnlocked, invalidateCosmeticsCache } from '../utils/arena/cosmeticsCache';
import { CloseButton } from './ui/navButtons';
import { playUI, UI } from '../utils/arena/uiSounds';
import { t } from '../localization/t';
import {
  getPackPool, pickStablePreviewCards, PortraitFanPreview, PORTRAIT_TILE_SIZE,
  CosmeticRevealModal,
} from './cosmeticPackPreview';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  BG_ATMOSPHERE, VIGNETTE, KnotOrnament,
} from '../lib/medievalTheme';

// Match the store's PortraitFanPreview layer count so the preview
// silhouette is consistent between surfaces.
const FAN_PREVIEW_COUNT = 5;

export default class SeasonVoucherPickerModal extends Component {
  state = {
    loading: true,
    error: null,
    packs: [],
    owned: new Set(),
    previewByPack: {},
    hoveredPackId: null,
    redeeming: false,
    // Single-entry reveal payload, shaped like the store's so the
    // shared CosmeticRevealModal renders the unlocked portrait with
    // the same "you got this" animation. `null` until the redemption
    // succeeds.
    reveal: null,
  };

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    this._unmounted = true;
  }

  load = async () => {
    this.setState({ loading: true, error: null });
    try {
      const data = await fetchCosmetics();
      if (this._unmounted) return;
      const packs = Array.isArray(data?.packs) ? data.packs : [];
      const owned = new Set(Array.isArray(data?.owned) ? data.owned : []);
      const sorceryCards = this.props.sorceryCards;
      const previewByPack = {};
      for (const pack of packs) {
        if (pack.kind !== 'portrait') continue;
        previewByPack[pack.id] = pickStablePreviewCards(
          getPackPool(sorceryCards, pack),
          FAN_PREVIEW_COUNT,
        );
      }
      this.setState({ packs, owned, previewByPack, loading: false });
    } catch (err) {
      if (this._unmounted) return;
      this.setState({ error: t('Failed to load cosmetic packs.'), loading: false });
    }
  };

  handlePick = async (pack) => {
    if (this.state.redeeming) return;
    const { sorceryCards, mailId } = this.props;
    const pool = getPackPool(sorceryCards, pack).filter((c) => !this.state.owned.has(c.unique_id));
    if (pool.length === 0) {
      toast.error(t('You already own every portrait in that pack. Try a different one.'));
      return;
    }
    const rolled = pool[Math.floor(Math.random() * pool.length)];

    this.setState({ redeeming: true });
    playUI(UI.GOLD);
    try {
      await redeemSeasonVoucher({ mailId, packId: pack.id, cardId: rolled.unique_id });
      noteCosmeticUnlocked(rolled.unique_id);
      invalidateCosmeticsCache();
      if (this._unmounted) return;
      // Reveal modal replaces the toast. Same surface the store uses
      // after a paid purchase. `onRedeemed` (which flips the mail row
      // to claimed and closes this picker) fires when the player
      // dismisses the reveal, not before, so they see their unlock.
      this.setState({ reveal: [{ pack, card: rolled }] });
    } catch (err) {
      const reason = err?.reason || 'unknown';
      const messages = {
        'voucher-already-claimed': t('This voucher has already been claimed.'),
        'voucher-not-found': t('The voucher mail could not be found.'),
        'voucher-not-yours': t('That voucher does not belong to your account.'),
        'already-owned': t('You already own that portrait. Try a different pack.'),
        'pack-not-portrait': t('That pack is not eligible for this voucher.'),
      };
      toast.error(messages[reason] || t('Redemption failed: {reason}', { reason }));
      if (this._unmounted) return;
      this.setState({ redeeming: false });
    }
  };

  handleRevealClose = () => {
    const { reveal } = this.state;
    const first = reveal?.[0];
    this.setState({ reveal: null });
    if (first) this.props.onRedeemed?.(first);
  };

  setHovered = (id) => {
    this.setState((s) => (s.hoveredPackId === id ? null : { hoveredPackId: id }));
  };

  clearHovered = (id) => {
    this.setState((s) => (s.hoveredPackId === id ? { hoveredPackId: null } : null));
  };

  renderPackTile = (pack) => {
    const { sorceryCards } = this.props;
    const { previewByPack, owned, redeeming, hoveredPackId } = this.state;

    const pool = getPackPool(sorceryCards, pack);
    const totalInPool = pool.length;
    const ownedInPool = pool.filter((c) => owned.has(c.unique_id)).length;
    const remaining = totalInPool - ownedInPool;
    const exhausted = totalInPool > 0 && remaining === 0;
    const previewCards = previewByPack[pack.id] || [];
    const hasPreview = previewCards.length > 0;
    const isHovered = hoveredPackId === pack.id && !exhausted;
    const disabled = redeeming || exhausted;

    return (
      <div key={pack.id} className="flex flex-col items-center">
        <div
          className="relative"
          style={{
            ...PORTRAIT_TILE_SIZE,
            // Hovered tile sits above its grid neighbours so the wider
            // fan isn't clipped by adjacent siblings.
            zIndex: isHovered ? 10 : 'auto',
          }}
        >
          <button
            type="button"
            className="relative block w-full h-full cursor-pointer disabled:cursor-not-allowed"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              opacity: exhausted ? 0.55 : 1,
            }}
            onClick={() => !disabled && this.handlePick(pack)}
            onMouseEnter={() => this.setHovered(pack.id)}
            onMouseLeave={() => this.clearHovered(pack.id)}
            disabled={disabled}
            title={pack.name}
          >
            {hasPreview ? (
              <PortraitFanPreview cards={previewCards} hovered={isHovered} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
                ?
              </div>
            )}
            {exhausted ? (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 20 }}>
                <span
                  className="text-[0.65cqw] font-bold arena-heading px-3 py-1"
                  style={{
                    color: '#7ad06a',
                    textShadow: '0 1px 3px rgba(0,0,0,0.95)',
                    background: 'rgba(8,6,4,0.85)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  {t('All Unlocked')}
                </span>
              </div>
            ) : null}
          </button>
        </div>

        {/* Name pill. Same treatment as the store tile so the two
            surfaces read as the same component. */}
        <div
          className="text-[0.75cqw] font-bold arena-heading mt-3 px-2.5 py-1 max-w-[15cqw] truncate"
          style={{
            color: '#e8d5a0',
            textShadow: '0 2px 6px rgba(0,0,0,0.9)',
            background: 'linear-gradient(180deg, rgba(14,11,8,0.85) 0%, rgba(8,6,4,0.9) 100%)',
            border: `1px solid ${GOLD} 0.32)`,
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.08), 0 2px 6px rgba(0,0,0,0.5)',
          }}
          title={pack.name}
        >
          {pack.name}
        </div>

        <div
          className="text-[0.55cqw] mt-1"
          style={{ color: exhausted ? '#7ad06a' : TEXT_MUTED }}
        >
          {t('{owned}/{total} owned', { owned: ownedInPool, total: totalInPool })}
        </div>

        {/* Where the store renders the price strip, the voucher tile
            renders a FREE badge so the affordance is consistent. */}
        <div
          className="mt-2 text-[0.65cqw] font-bold"
          style={{ color: exhausted ? TEXT_MUTED : ACCENT_GOLD }}
        >
          {t('Free')}
        </div>
      </div>
    );
  };

  render() {
    const { onClose } = this.props;
    const { loading, error, packs, redeeming, reveal } = this.state;
    const portraitPacks = packs.filter((p) => p.kind === 'portrait');

    return (
      <div
        className="fixed inset-0 z-[110] flex flex-col"
        style={{ background: `url("/tex-noise.webp"), ${BG_ATMOSPHERE}` }}
      >
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative flex-1 flex flex-col min-h-0">
          {/* Header */}
          <div className="relative shrink-0 flex items-center justify-center pt-10 pb-4 px-12">
            <div className="flex items-center gap-8" aria-hidden="true">
              <KnotOrnament side="left" size={32} />
              <div className="h-px w-24" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.5) 100%)` }} />
              <h1
                className="arena-heading font-bold"
                style={{
                  color: TEXT_PRIMARY,
                  fontSize: '2cqw',
                  textShadow: '0 3px 8px rgba(0,0,0,0.8), 0 0 22px rgba(212,168,67,0.3)',
                }}
              >
                {t('Choose Your Portrait Pack')}
              </h1>
              <div className="h-px w-24" style={{ background: `linear-gradient(90deg, ${GOLD} 0.5) 0%, transparent 100%)` }} />
              <KnotOrnament side="right" size={32} />
            </div>
            <div className="absolute right-12 top-10">
              <CloseButton title={t('Close')} onClick={onClose} />
            </div>
            <div
              className="absolute left-12 top-10 flex flex-col text-[0.625cqw] font-semibold"
              style={{ color: TEXT_MUTED }}
            >
              <span>{t('Season Voucher')}</span>
              <span style={{ color: ACCENT_GOLD }}>{t('Top 10 reward')}</span>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-12 pb-12">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <RuneSpinner size={48} />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-[0.875cqw]" style={{ color: '#f87171' }}>
                {error}
              </div>
            ) : portraitPacks.length === 0 ? (
              <div className="text-center py-12 text-[0.875cqw]" style={{ color: TEXT_MUTED }}>
                {t('No portrait packs available right now.')}
              </div>
            ) : (
              <>
                <p
                  className="text-center text-[0.75cqw] mb-8 max-w-[60cqw] mx-auto"
                  style={{ color: TEXT_BODY }}
                >
                  {t("Pick any portrait pack. It's yours, free. We'll roll a random portrait from the pack's pool the same way the store does, skipping anything you already own.")}
                </p>
                <div
                  className="grid mx-auto"
                  style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(14cqw, max-content))',
                    justifyContent: 'center',
                    columnGap: '2.5cqw',
                    rowGap: '3cqw',
                    maxWidth: '80cqw',
                  }}
                >
                  {portraitPacks.map(this.renderPackTile)}
                </div>
                {redeeming ? (
                  <div className="text-center mt-6 text-[0.75cqw]" style={{ color: TEXT_MUTED }}>
                    {t('Redeeming…')}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <CosmeticRevealModal reveal={reveal} onClose={this.handleRevealClose} />
      </div>
    );
  }
}
